import _ from "lodash";
import { AURA_STATEMENT } from "../config/const/enums";
import { isDropDownNumericQuestion } from "../schema/auraQuestionsSchema";

export function getAzureJWTToken() {
	return sessionStorage.getItem("azurejwttoken");
}

export function getAuraAccessTokenFromStorage() {
	return sessionStorage.getItem("auraAccessToken");
}

export function getAuraAPIUrlFromStorage() {
	return sessionStorage.getItem("auraEndpoint");
}

export function getHealthQuestionIDDetails(allQuestions) {
	let auraQuestionIdList = [];
	if (allQuestions) {
		_.forEach(allQuestions, function (question) {
			let auraQuestionObj = {};
			if (question?.id) {
				auraQuestionObj.id = question.id;
				auraQuestionObj.componentType = question.constraints?.componentType;
				auraQuestionIdList.push(auraQuestionObj);
			}
		});
		return auraQuestionIdList;
	}
}

export function getOccupationMapping(previousAuraQuestion) {
	if (previousAuraQuestion?.answer?.answers) {
		let returnOccupationMapping = {};
		const answerOccupation = _.first(previousAuraQuestion?.answer?.answers);
		returnOccupationMapping.label = answerOccupation.text;
		returnOccupationMapping.value = answerOccupation.value;
		return returnOccupationMapping;
	}
}

export function getUnitizedValue(previousAuraQuestion) {
	if (isDropDownNumericQuestion(previousAuraQuestion)) {
		return {
			unitizedValue: _.first(previousAuraQuestion?.answer?.answerParts),
			unitizedOption: previousAuraQuestion?.answer?.selectedUnit,
		};
	} else {
		return previousAuraQuestion?.answer?.answerParts[0];
	}
}

export function getPreviousAnsweredValue(previousAuraQuestion) {
	switch (previousAuraQuestion?.type) {
		case "SINGLE_CHOICE":
		case "TEXT":
		case "NUMERIC":
		case "UNITIZED_CHOICE":
		case "DATE_CHOICE":
		case "NUMERIC_CHOICE":
			return previousAuraQuestion?.answer?.value;
		case "UNITIZED":
			return getUnitizedValue(previousAuraQuestion);
		case "MULTIPLE_CHOICE":
			return _.map(previousAuraQuestion?.answer?.answers, "value");
		case "SEARCH":
			return getOccupationMapping(previousAuraQuestion);
		case "BLOOD_PRESSURE":
			const lastBPreading = _.first(previousAuraQuestion?.answer?.readings);
			return {
				systolicReading: lastBPreading?.systolic,
				diastolicReading: lastBPreading?.diastolic,
			};
		case "DATE":
			return new Date(previousAuraQuestion?.answer?.value);
		default:
			return null;
	}
}

export function getPreviousAuraQuestion(allQuestionsList, currentQuestionId) {
	let returnPreviousQuestion = {};
	if (currentQuestionId) {
		const currentQuestionIndex = _.findIndex(allQuestionsList.responseJson, [
			"id",
			currentQuestionId,
		]);
		if (currentQuestionIndex > 0) {
			returnPreviousQuestion = _.nth(
				allQuestionsList.responseJson,
				currentQuestionIndex - 1
			);
			returnPreviousQuestion.etag = allQuestionsList.etag;
		}
		return returnPreviousQuestion;
	}
}

function getQuestionBasedOnID(allQuestionsList, questionID) {
	return _.find(allQuestionsList, { id: parseInt(questionID) });
}

export function getTheLatestQuestion(allQuestionsList, currentQuestionId) {
	let returnNextQuestion = {};
	if (currentQuestionId) {
		const currentQuestionIndex = _.findIndex(allQuestionsList.responseJson, [
			"id",
			currentQuestionId,
		]);
		if (
			currentQuestionIndex !== -1 &&
			currentQuestionIndex + 1 < allQuestionsList.responseJson.length
		) {
			returnNextQuestion = _.nth(
				allQuestionsList.responseJson,
				currentQuestionIndex + 1
			);
			returnNextQuestion.etag = allQuestionsList.etag;
		}
	} else {
		let allQuestionNotAnsweredList = _.filter(allQuestionsList.responseJson, [
			"isAnswered",
			false,
		]);
		if (allQuestionNotAnsweredList.length > 0) {
			returnNextQuestion = _.first(allQuestionNotAnsweredList);
			returnNextQuestion.etag = allQuestionsList.etag;
		} else {
			// this will come in a state when the user has completed UW questions and click on edit on the review page
			// search for the question ID in the session storage
			const editQuestionID = sessionStorage.getItem("editquestionid");
			if (editQuestionID) {
				returnNextQuestion = getQuestionBasedOnID(
					allQuestionsList.responseJson,
					editQuestionID
				);
				returnNextQuestion.etag = allQuestionsList.etag;
			} else {
				// if editquestionid is not there take to the last answered question
				returnNextQuestion = _.last(allQuestionsList.responseJson);
				returnNextQuestion.etag = allQuestionsList.etag;
			}
		}
	}
	return returnNextQuestion;
}

// This is for local dev build purpose
export function getLatestQuestionWithComponentType(
	allQuestionsList,
	componentType
) {
	let allQuestionNotAnsweredList = _.filter(allQuestionsList.responseJson, [
		"type",
		componentType,
	]);
	if (allQuestionNotAnsweredList) {
		return _.first(allQuestionNotAnsweredList);
	}
}
function getQuestionContext(context) {
	return context?.length > 0 ? _.first(context) : "";
}

function getQuestionStatement(externalId) {
	if (externalId != null && externalId.includes("MedicalStatementEver")) {
		return AURA_STATEMENT.MEDICAL_STATEMENT_EVER;
	} else if (
		externalId != null &&
		externalId.includes("MedicalStatementLast5Years")
	) {
		return AURA_STATEMENT.MEDICAL_STATEMENT_LAST_5YEARS;
	}
}

function isYesNoRadioOptions(choices) {
	// show the toggle button with "Yes" "No" and "other value" (max length of the character is 9)
	const yesNoFlag =
		_.filter(choices, (choice) =>
			_.some([choice.text], (option) => option === "Yes" || option === "No")
		).length === 2;

	const choicesWithLongText = _.filter(
		choices,
		(choice) => choice.text.length > 10
	);
	const isAllChoicesShort = choicesWithLongText.length === 0;
	return yesNoFlag && isAllChoicesShort && choices.length <= 3;
}

export function getMappedQuestionResponse(auraQuestion) {
	let returnMappedQuestionObj = {};
	let answerOptionList = [];
	if (auraQuestion) {
		const hasYesAndNoOptions = isYesNoRadioOptions(
			auraQuestion?.constraints?.choices
		);
		returnMappedQuestionObj.hasYesAndNoOptions = hasYesAndNoOptions;
		returnMappedQuestionObj.richText = auraQuestion.richText;
		returnMappedQuestionObj.id = auraQuestion.id;
		returnMappedQuestionObj.richHelpText = auraQuestion.richHelpText;
		returnMappedQuestionObj.context = getQuestionContext(auraQuestion?.context);
		returnMappedQuestionObj.statement = getQuestionStatement(
			auraQuestion?.externalId
		);
		_.forEach(auraQuestion?.constraints?.choices, function (choice) {
			let answerOption = {};
			if (choice?.richText) {
				if (hasYesAndNoOptions) {
					answerOption.label = choice.text;
				} else {
					answerOption.name = choice.text;
				}
				answerOption.value = choice.value;
				answerOptionList.push(answerOption);
			}
		});
		returnMappedQuestionObj.answerOptionList = answerOptionList;
	}
	return returnMappedQuestionObj;
}

export function getMappedQuestionRadioResponse(auraQuestion) {
	let returnMappedQuestionObj = {};
	let answerOptionList = [];
	if (auraQuestion) {
		returnMappedQuestionObj.richText = auraQuestion.text;
		returnMappedQuestionObj.richHelpText = auraQuestion.richHelpText;
		returnMappedQuestionObj.context = getQuestionContext(auraQuestion?.context);
		returnMappedQuestionObj.statement = getQuestionStatement(
			auraQuestion?.externalId
		);
		_.forEach(auraQuestion?.constraints?.choices, function (choice) {
			let answerOption = {};
			if (choice?.richText) {
				answerOption.name = choice.text;
				answerOption.value = choice.value;
				answerOptionList.push(answerOption);
			}
		});
		returnMappedQuestionObj.answerOptionList = answerOptionList;
	}
	return returnMappedQuestionObj;
}

export function getMappedQuestionDateResponse(auraQuestion) {
	let returnMappedQuestionObj = {};
	if (auraQuestion) {
		returnMappedQuestionObj.richText = auraQuestion.richText;
		returnMappedQuestionObj.richHelpText = auraQuestion.richHelpText;
		returnMappedQuestionObj.context = getQuestionContext(auraQuestion?.context);
		returnMappedQuestionObj.statement = getQuestionStatement(
			auraQuestion?.externalId
		);
		if (auraQuestion?.constraints?.availableDateFormats) {
			const unitLabel = auraQuestion.constraints.availableDateFormats[0];
			if (unitLabel === "MONTH_YEAR") {
				returnMappedQuestionObj.placeholderOne = "Years";
				returnMappedQuestionObj.placeholderTwo = "Months";
			} else {
				returnMappedQuestionObj.placeholderOne = "";
				returnMappedQuestionObj.placeholderTwo = "";
			}
		}
	}
	return returnMappedQuestionObj;
}

function getAvailableUnitsDropdown(auraQuestion) {
	let availableUnitList = [];
	_.forEach(auraQuestion?.constraints?.availableUnits, function (units) {
		let dropdownOption = {};
		if (units?.unitSymbol) {
			dropdownOption.name = units.label;
			dropdownOption.value = units.unitSymbol;
			availableUnitList.push(dropdownOption);
		}
	});
	return availableUnitList;
}

export function getMappedQuestionTextResponse(auraQuestion) {
	let returnMappedQuestionObj = {};
	if (auraQuestion) {
		returnMappedQuestionObj.richText = auraQuestion.richText;
		returnMappedQuestionObj.richHelpText = auraQuestion.richHelpText;
		returnMappedQuestionObj.id = auraQuestion.id;
		returnMappedQuestionObj.context = getQuestionContext(auraQuestion?.context);
		returnMappedQuestionObj.statement = getQuestionStatement(
			auraQuestion?.externalId
		);
	}
	return returnMappedQuestionObj;
}

export function getMappedQuestionUnitizedResponse(auraQuestion) {
	let returnMappedQuestionObj = {};
	if (auraQuestion) {
		returnMappedQuestionObj.richText = auraQuestion.richText;
		returnMappedQuestionObj.richHelpText = auraQuestion.richHelpText;
		returnMappedQuestionObj.id = auraQuestion.id;
		returnMappedQuestionObj.context = getQuestionContext(auraQuestion?.context);
		returnMappedQuestionObj.statement = getQuestionStatement(
			auraQuestion?.externalId
		);
		if (auraQuestion?.constraints?.availableUnits?.length > 1) {
			returnMappedQuestionObj.isDropdownNumeric = true;
			returnMappedQuestionObj.availableUnitDropdown =
				getAvailableUnitsDropdown(auraQuestion);
		} else {
			returnMappedQuestionObj.isDropdownNumeric = false;
			if (auraQuestion?.constraints?.availableUnits) {
				const unitLabel = _.first(auraQuestion.constraints.availableUnits);
				returnMappedQuestionObj.labelText = unitLabel.label;
			}
		}
	}
	return returnMappedQuestionObj;
}

export function getMappedQuestionSearchResponse(auraQuestion) {
	let returnMappedQuestionObj = {};
	if (auraQuestion) {
		returnMappedQuestionObj.richText = auraQuestion.richText;
		returnMappedQuestionObj.richHelpText = auraQuestion.richHelpText;
		returnMappedQuestionObj.id = auraQuestion.id;
		returnMappedQuestionObj.context = getQuestionContext(auraQuestion?.context);
		returnMappedQuestionObj.statement = getQuestionStatement(
			auraQuestion?.externalId
		);
	}
	return returnMappedQuestionObj;
}

export function getPatchAnswerPayload(nextAuraQuestion, formData) {
	let returnPayload = {};
	let reqPayload = {};
	switch (nextAuraQuestion?.type) {
		case "SINGLE_CHOICE":
			let formAnswerPayload = _.filter(nextAuraQuestion?.constraints?.choices, [
				"value",
				formData.healthQuestion,
			]);
			if (formAnswerPayload.length > 0) {
				returnPayload.answer = formAnswerPayload[0];
			}
			return returnPayload;
		case "UNITIZED":
			reqPayload.questionType = nextAuraQuestion.type;
			reqPayload.selectedUnit = isDropDownNumericQuestion(nextAuraQuestion)
				? formData.unitizedOption
				: nextAuraQuestion.constraints.availableUnits[0].unitSymbol;
			reqPayload.answerParts = isDropDownNumericQuestion(nextAuraQuestion)
				? [formData.unitizedValue]
				: [formData.healthQuestion];
			reqPayload.unknownAnswer = nextAuraQuestion.constraints.canBeUnknown;
			returnPayload.answer = reqPayload;
			return returnPayload;
		case "MULTIPLE_CHOICE":
			const filteredChoiceData = _.filter(
				nextAuraQuestion?.constraints?.choices,
				(choice) => {
					return _.includes(formData.healthQuestion, choice.value);
				}
			);
			reqPayload.questionType = nextAuraQuestion.type;
			reqPayload.answers = filteredChoiceData;
			let unknownFlag = getUnknownAnswerFlag(filteredChoiceData);
			reqPayload.unknownAnswer = unknownFlag;
			returnPayload.answer = reqPayload;
			return returnPayload;
		case "DATE":
			const selectedDate = formData.healthQuestion;
			const seleactedYear = selectedDate.getFullYear();
			const selectedMonth = (selectedDate.getMonth() + 1)
				.toString()
				.padStart(2, "0");
			reqPayload.questionType = nextAuraQuestion.type;
			reqPayload.dateFormat = "MONTH_YEAR";
			reqPayload.unknownAnswer = nextAuraQuestion.constraints.canBeUnknown;
			reqPayload.value = `${seleactedYear}-${selectedMonth}`;
			returnPayload.answer = reqPayload;
			return returnPayload;
		case "NUMERIC":
		case "TEXT":
			reqPayload.questionType = nextAuraQuestion.type;
			reqPayload.unknownAnswer = nextAuraQuestion.constraints.canBeUnknown;
			reqPayload.value = formData.healthQuestion;
			returnPayload.answer = reqPayload;
			return returnPayload;
		case "NUMERIC_CHOICE":
		case "DATE_CHOICE":
		case "UNITIZED_CHOICE":
			let selectedChoiceAnswer = _.filter(
				nextAuraQuestion?.constraints?.choices,
				["value", formData.healthQuestion]
			);
			if (selectedChoiceAnswer.length > 0) {
				returnPayload.answer = selectedChoiceAnswer[0];
			}
			return returnPayload;
		case "SEARCH":
			let searchAnswer = {};
			let occupationList = [];
			reqPayload.questionType = nextAuraQuestion.type;
			searchAnswer.text = formData.healthQuestion.label;
			searchAnswer.value = formData.healthQuestion.value;
			occupationList.push(searchAnswer);
			reqPayload.answers = occupationList;
			returnPayload.answer = reqPayload;
			return returnPayload;
		case "BLOOD_PRESSURE":
			reqPayload.questionType = nextAuraQuestion.type;
			reqPayload.unknownAnswer = nextAuraQuestion.constraints.canBeUnknown;
			reqPayload.readings = [
				{
					systolic: formData?.systolicReading,
					diastolic: formData?.diastolicReading,
				},
			];
			returnPayload.answer = reqPayload;
			return returnPayload;
		default:
			return {
				message: "No option selected",
				component: null,
			};
	}

	function getUnknownAnswerFlag(filteredChoiceData) {
		let unknownTrueAnswer = _.filter(filteredChoiceData, [
			"unknownAnswer",
			true,
		]);
		if (
			unknownTrueAnswer.length > 0 &&
			unknownTrueAnswer.length == filteredChoiceData.length
		) {
			return true;
		} else {
			return false;
		}
	}
}

export const checkAuraUWcompleted = (auraQuestion) => {
	return auraQuestion["_links"]?.sessionStatus?.completed;
};

export const checkAuraUWSubmitted = (auraQuestion) => {
	return auraQuestion["_links"]?.sessionStatus?.submitted;
};
